import { MobergAnimationTimingMs } from "../../../../../../../../Moberg";
import { valueOrMissing } from "../../../../utils";
import { D3BoxPlotConfig } from "../../../D3/D3BoxPlot";
import { D3ClipPathConfig } from "../../../D3/D3ClipPath";
import { D3HorizontalLinesConfig } from "../../../D3/D3HorizontalLines";
import { D3RectConfig } from "../../../D3/D3Rect";
import { D3LinearXAxisConfig } from "../../../D3/D3LinearXAxis";
import { D3ParabolaConfig } from "../../../D3/D3Parabola";
import { D3SummaryValuesConfig } from "../../../D3/D3SummaryValues";
import { D3ValueAxisConfig } from "../../../D3/D3ValueAxis";
import { D3TimelineConfig } from "../../../D3/Timeline/D3Timeline";
import { ConfigurationBuilder } from "../../D3ConfigurationBuilder"
import { LEFT_MARGIN, RIGHT_MARGIN } from "../../Constants";
import { D3LineConfig } from "../../../D3/D3Line";
import { getDataInView } from "../../../../Data/TimeSeriesData";
import { DataSource } from "../../../../Types/DataSource";
import { D3MAPOptPlot } from "./D3MAPOptPlot";
import { D3ErrorBarsConfig } from "../../CPPOpt/D3/D3CPPOptErrorBars";
import { range } from "lodash";
import { Analytic } from "../../../../Types/AnalysisDetails";

export class D3MAPOptPlotConfigurationBuilder extends ConfigurationBuilder<D3MAPOptPlot> {
	private bins = range(21, 121, 2)
	public transitionDuration: number = MobergAnimationTimingMs.SLOW
	private coxSignal: number[] = []
	private mapoptSignal: number[] = []

	getClipPathConfig = (): D3ClipPathConfig => {
		return {
			id: this.getClipPathId(),
			boundingBox: this.visualization.curveFitBoundingBox
		}
	}

	getTimelineConfig = (): D3TimelineConfig => {
		const timelineUpdateTimes = (start: number, end: number) => {
			this.visualization.viewTimesChanged(start, end)
			this.visualization.updateLinkedWindows()
		}

		return {
			id: this.visualization.config.id,
			viewScale: this.visualization.config.viewScale,
			width: this.visualization.config.dimensions.width - LEFT_MARGIN - RIGHT_MARGIN,
			annotations: [],
			fileScale: this.visualization.config.fileScale,
			playbackSpeed: this.visualization.config.playbackSpeed,
			currentTimelineController: this.visualization.config.timelineController,
			isLinked: this.visualization.config.isLinked,
			timeZone: this.visualization.config.timeZone,
			liveModeEnabled: this.visualization.config.liveModeEnabled,
			onDrag: this.visualization.onTimelineSliderDrag,
			onDragEnd: this.visualization.onTimelineSliderDragEnd,
			updateViewTimes: timelineUpdateTimes,
			goToStart: this.visualization.goToStart,
			goToEnd: this.visualization.goToEnd,
			goToNextPage: this.visualization.goToNextPage,
			goToPreviousPage: this.visualization.goToPreviousPage,
		}
	}

	getXAxisConfig = (): D3LinearXAxisConfig => ({
		scale: this.visualization.xScaleLinear,
		boundingBox: this.visualization.curveFitBoundingBox,
		label: "MAP (mmHg)",
	})

	getYAxisConfig = (): D3ValueAxisConfig => ({
		graphId: this.visualization.config.id,
		scale: this.visualization.yScale,
		label: "COx",
		onDrag: this.visualization.onYAxisDrag,
		onDragEnd: this.visualization.onYAxisDragEnd,
		onDragStart: this.visualization.onYAxisDragStart,
		onDoubleClick: this.visualization.autoScale
	})

	getHorizontalLinesConfig = (): D3HorizontalLinesConfig => ({
		yScale: this.visualization.yScale,
		width: this.visualization.curveFitBoundingBox.width,
	})

	getParabolaConfig = (): D3ParabolaConfig => ({
		xScale: this.visualization.xScaleLinear,
		yScale: this.visualization.yScale,
		coefficients: this.visualization.mapoptData.coefficients,
		clipPathId: this.getClipPathId(),
		color: "var(--color-chart-axis-text, black)",
		isDashed: false,
		transitionDuration: this.transitionDuration,
		applyInverseFisher: true
	})

	getErrorBarsConfig = (): D3ErrorBarsConfig => ({
		yScale: this.visualization.yScale,
		xScale: this.visualization.xScaleLinear,
		clipPathId: this.getClipPathId(),
		bars: this.bins.map((x, index) => ({
			x,
			y: this.visualization.mapoptData.yValues[index],
			error: this.visualization.mapoptData.errors[index],
			includedInFit: this.visualization.mapoptData.includedInFit[index] === 1,
			dataPercentage: this.visualization.mapoptData.dataPercentages[index],
		})).filter(bar => bar.y != null),
		transitionDuration: this.transitionDuration
	})

	getCOxLineConfig = (): D3LineConfig => ({
		x1: 0,
		x2: this.visualization.xScaleLinear.range()[1],
		y1: this.visualization.yScale(this.visualization.config.coxThreshold),
		y2: this.visualization.yScale(this.visualization.config.coxThreshold),
		color: "gray",
		clipPathId: this.getClipPathId(),
		isDashed: false,
		transitionDuration: this.transitionDuration,
		strokeWidth: 1
	})

	getLLARectConfig = (): D3RectConfig => ({
		x: this.visualization.xScaleLinear(this.visualization.mapoptData.LLA ?? this.visualization.xScaleLinear.domain()[0]),
		y: 0,
		width: (this.visualization.mapoptData.LLA && this.visualization.mapoptData.MAPOpt) ? this.visualization.xScaleLinear(this.visualization.mapoptData.MAPOpt!) - this.visualization.xScaleLinear(this.visualization.mapoptData.LLA!) : 0,
		height: this.visualization.yScale.range()[0],
		color: "#EA00FF",
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
		opacity: 0.1
	})

	getULARectConfig = (): D3RectConfig => ({
		x: this.visualization.xScaleLinear(this.visualization.mapoptData.MAPOpt ?? this.visualization.xScaleLinear.domain()[0]),
		y: 0,
		width: (this.visualization.mapoptData.ULA && this.visualization.mapoptData.MAPOpt) ? this.visualization.xScaleLinear(this.visualization.mapoptData.ULA!) - this.visualization.xScaleLinear(this.visualization.mapoptData.MAPOpt!) : 0,
		height: this.visualization.yScale.range()[0],
		color: "#EA00FF",
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
		opacity: 0.1
	})

	getMAPOptLineConfig = (): D3LineConfig => ({
		x1: this.visualization.xScaleLinear(this.visualization.mapoptData.MAPOpt ?? this.visualization.xScaleLinear.domain()[0]),
		x2: this.visualization.xScaleLinear(this.visualization.mapoptData.MAPOpt ?? this.visualization.xScaleLinear.domain()[0]),
		y1: this.visualization.yScale.range()[0],
		y2: this.visualization.yScale.range()[1],
		color: "#EA00FF",
		isDashed: false,
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
		strokeWidth: 3
	})

	getSummaryValuesConfig = (): D3SummaryValuesConfig => {
		const textColor = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black"
		return {
			x: 0,
			y: 0,
			width: this.visualization.config.dimensions.width/8,
			height: this.visualization.curveFitBoundingBox.height,
			values: [
				{
					name: "MAPopt",
					value: valueOrMissing(this.visualization.mapoptData.MAPOpt),
					color: "#EA00FF",
					precision: 0,
					isVisible: true,
				},
				{
					name: "LLA",
					value: valueOrMissing(this.visualization.mapoptData.LLA),
					color: textColor,
					precision: 0,
					isVisible: true,
				},
				{
					name: "ULA",
					value: valueOrMissing(this.visualization.mapoptData.ULA),
					color: textColor,
					precision: 0,
					isVisible: true,
				},
			],
			rows: 3,
			columns: 1,
		}
	}

	getPercentagesSummaryValuesConfig = (): D3SummaryValuesConfig => {
		const [startDate, endDate] = this.visualization.config.viewScale.domain()
		const totalDurationMinutes = (endDate.getTime() - startDate.getTime()) / (60 * 1000)
		const optAvailability = 100 * this.mapoptSignal.length / totalDurationMinutes
		const timeAboveCOxThreshold = 100 * (this.coxSignal.filter(value => value > this.visualization.config.coxThreshold).length / totalDurationMinutes)
		const textColor = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black"

		return {
			x: 0,
			y: 0,
			width: this.visualization.config.dimensions.width/2,
			height: this.visualization.config.dimensions.height/8,
			values: [
				{
					name: "% time MAPopt calculable",
					value: optAvailability,
					color: textColor,
					precision: 0,
					isVisible: true,
					isPercent: true
				},
				{
					name: `% time > COx threshold (${this.visualization.config.coxThreshold})`,
					value: timeAboveCOxThreshold,
					color: textColor,
					precision: 0,
					isVisible: true,
					isPercent: true
				},
			],
			rows: 1,
			columns: 2,
		}
	}

	getMAPoptBoxPlotConfig = (): D3BoxPlotConfig => ({
		color: "#EA00FF",
		boundingBox: this.visualization.mapoptBoxPlotBoundingBox,
		scale: this.visualization.mapoptBoxPlotYScale,
		yLabel: "MAPopt",
		title: `${this.visualization.config.mapoptAlgorithm === Analytic.MAPOpt ? "MAPopt" : "MAPopt (weighted)"}`,
		viewScale: this.visualization.config.viewScale,
		precision: 0,
		values: this.mapoptSignal
	})

	getCOxBoxPlotConfig = (): D3BoxPlotConfig => ({
		color: "#FF0000",
		boundingBox: this.visualization.coxBoxPlotBoundingBox,
		scale: this.visualization.coxBoxPlotYScale,
		yLabel: "COx",
		title: "COx",
		viewScale: this.visualization.config.viewScale,
		precision: 1,
		values: this.coxSignal
	})

	private getClipPathId = () => `d3-clip-path-${this.visualization.config.id}`

	public readMAPOptSignal() {
		this.mapoptSignal = getDataInView(
			this.visualization.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number,
			this.visualization.pageManager,
			{
				id: "MAPopt",
				name: "MAPopt",
				units: "mmHg",
				dataSource: DataSource.CURRENT_PATIENT,
				rawDataKey: "MAPopt",
				dataKey: "MAPOpt,Composite,SampleSeries",
				onDemandAnalysis: this.visualization.getMAPoptAnalysis(),
				isCompositePart: true,
				compositeIndex: 0
			},
			this.visualization.config.viewScale
		)
	}

	public readCOxSignal() {
		this.coxSignal = getDataInView(
			this.visualization.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number,
			this.visualization.pageManager,
			{
				id: "COx",
				name: "COx",
				units: "",
				dataSource: DataSource.CURRENT_PATIENT,
				rawDataKey: "COx",
				dataKey: "COx",
				onDemandAnalysis: this.visualization.getCOxAnalysis(),
				isCompositePart: false
			},
			this.visualization.config.viewScale
		)
	}
}